// formatMoney comes from assets/money.js (window.Zahnheld.formatMoney),
// loaded via theme.liquid with defer before this script. Defensive fallback
// returns raw cents as a string if money.js failed to load.
function formatPrice(cents, format) {
    const helper = window.Zahnheld && window.Zahnheld.formatMoney;
    if (!helper) {
        console.error('[upsell-manager] money.js not loaded; falling back to raw cents');
        return String(cents);
    }
    return helper(cents, format);
}

// --- Pure pricing logic (no DOM) -------------------------------------------
// Extracted so the tier mapping and price math can be unit tested in isolation.
// Exposed on window.__upsellPricing (see bottom of file).

// Maps the number of selected upsells to a tier (1-3). Tier 1 is the default
// for 0 or 1 selections; 2 selections -> tier 2; 3 or more -> tier 3.
function tierForCount(count) {
    if (count >= 3) return 3;
    if (count === 2) return 2;
    return 1;
}

// Computes the discounted upsell price for one item.
//
// The tier discount stacks on top of any existing discount: the basis is the
// code-preview price when it beats the original, otherwise the auto/VIP
// percentage-reduced price, otherwise the original. The tier percentage is then
// applied to that basis. All cent values are integers (Math.round).
//
// @param {object} opts
//   originalPriceCents {number}  - undiscounted unit price in cents
//   tierPercent        {number}  - tier discount percentage (0-100)
//   codeFinalCents     {number=} - code-preview final price, or null/undefined
//   autoDiscount       {object=} - { valueType: 'percentage', percent } or null
// @returns {{ newPriceCents:number, totalSavingsCents:number, effectivePercent:number }}
function computeUpsellPrice(opts) {
    const originalPriceCents = opts.originalPriceCents;
    const tierPercent = opts.tierPercent;
    const codeFinalCents = opts.codeFinalCents;
    const autoDiscount = opts.autoDiscount;

    let tierBasisCents = originalPriceCents;
    if (codeFinalCents != null && codeFinalCents < originalPriceCents) {
        tierBasisCents = codeFinalCents;
    } else if (autoDiscount && autoDiscount.valueType === 'percentage') {
        tierBasisCents = Math.round((originalPriceCents * (100 - autoDiscount.percent)) / 100);
    }

    const tierDiscountAmount = (tierBasisCents * tierPercent) / 100;
    const newPriceCents = Math.round(tierBasisCents - tierDiscountAmount);

    const totalSavingsCents = originalPriceCents - newPriceCents;
    const effectivePercent = originalPriceCents > 0
        ? Math.round((totalSavingsCents * 100) / originalPriceCents)
        : 0;

    return {
        newPriceCents: newPriceCents,
        totalSavingsCents: totalSavingsCents,
        effectivePercent: effectivePercent
    };
}

class UpsellManager {
    constructor(formId, container) {
        this.form = document.getElementById(formId);
        this.container = container;
        this._abort = new AbortController();

        // Load Tier Configuration
        this.tiers = {
            1: {
                code: this.container.getAttribute('data-tier-1-code'),
                percent: Number(this.container.getAttribute('data-tier-1-percent')) || 0
            },
            2: {
                code: this.container.getAttribute('data-tier-2-code'),
                percent: Number(this.container.getAttribute('data-tier-2-percent')) || 0
            },
            3: {
                code: this.container.getAttribute('data-tier-3-code'),
                percent: Number(this.container.getAttribute('data-tier-3-percent')) || 0
            }
        };

        this.init();
    }

    init() {
        if (!this.form) return;

        const signal = this._abort.signal;

        // Use capture to intercept the event before other listeners
        this.form.addEventListener('submit', this.handleSubmit.bind(this), { capture: true, signal });

        // Listen for variant changes
        this.form.addEventListener('variant:change', this.handleVariantChange.bind(this), { signal });

        // Listen for upsell selection changes
        this.container.addEventListener('change', (e) => {
            if (e.target.matches('[data-upsell-checkbox]')) {
                this.handleSelectionChange();
            }
        }, { signal });

        const currentVariantId = this.form.querySelector('[name="id"]')?.value;
        if (currentVariantId) {
            this.updateVisibility(Number(currentVariantId));
        }

        this.handleSelectionChange();

        // Re-render Preise wenn ein Rabattcode aktiv/inaktiv wird.
        // signal sorgt dafür, dass der Listener bei destroy() automatisch entfernt
        // wird und keine detached Container am Event-Bus hängen bleiben.
        document.documentElement.addEventListener('discount:updated', () => this.handleSelectionChange(), { signal });
    }

    destroy() {
        if (this._abort) this._abort.abort();
    }

    handleVariantChange(event) {
        const variant = event.detail.variant;
        if (!variant) return;
        this.updateVisibility(variant.id);
        this.handleSelectionChange(); // Re-evaluate whenever variant changes (visibility might change selection availability)
    }

    handleSelectionChange() {
        const selectedCheckboxes = this.getSelectedUpsells();
        const count = selectedCheckboxes.length;
        const maxItems = 3;

        // 1. Enforce Limit
        const allCheckboxes = this.getVisibleCheckboxes();
        allCheckboxes.forEach(cb => {
            if (!cb.checked) {
                if (count >= maxItems) {
                    cb.disabled = true;
                    cb.parentElement.classList.add('opacity-50', 'cursor-not-allowed');
                } else {
                    cb.disabled = false;
                    cb.parentElement.classList.remove('opacity-50', 'cursor-not-allowed');
                }
            }
        });

        // 2. Determine Applicable Discount
        const currentTier = tierForCount(count);
        const discountPercent = this.tiers[currentTier]?.percent || 0;

        // 3. Update Visuals
        this.updateVisualPrices(discountPercent);
    }

    updateVisualPrices(percent) {
        const visibleContainer = this.getVisibleContainer();
        if (!visibleContainer) return;

        const moneyFormat = this.container.getAttribute('data-money-format');
        const items = visibleContainer.querySelectorAll('.upsell-item');

        // Basis für den Tier-Discount (stacking visuell): bei aktivem Code die
        // Code-Preview-Preise, bei kundengebundenem/automatischem Rabatt der um
        // den Prozentsatz reduzierte Preis. Upsell-Items sind KEINE price-list-
        // Elemente, daher rechnen wir den Auto-Rabatt selbst aus
        // data-original-price statt getPreview zu nutzen (das findet keine
        // Basispreise für Upsell-Varianten).
        const dp = (window.discountPreview && window.discountPreview.enabled) ? window.discountPreview : null;
        const hasCodes = dp && dp.getActiveCodes().length > 0;
        const autoDiscount = (dp && typeof dp.getAutoDiscount === 'function') ? dp.getAutoDiscount() : null;

        const variantIds = Array.from(items)
            .map(item => item.querySelector('.upsell-item__price'))
            .filter(p => p && p.dataset.variantId)
            .map(p => Number(p.dataset.variantId));

        const codePreview = hasCodes ? dp.getPreview(variantIds) : {};

        items.forEach(item => {
            const priceContainer = item.querySelector('.upsell-item__price');
            const priceDisplay = priceContainer.querySelector('.upsell-price-display');
            const comparePrice = priceContainer.querySelector('.upsell-compare-price');
            const badge = priceContainer.querySelector('.upsell-discount-badge');

            if (!priceDisplay || !priceContainer.dataset.originalPrice) return;

            const originalPriceCents = Number(priceContainer.dataset.originalPrice);
            const variantId = Number(priceContainer.dataset.variantId);
            const codeInfo = codePreview[variantId];

            // Tier-discount basis: the code-preview price if present, else the
            // price reduced by the auto/VIP percentage, else the original. Pure
            // math lives in computeUpsellPrice (above, unit tested).
            const { newPriceCents, effectivePercent } = computeUpsellPrice({
                originalPriceCents: originalPriceCents,
                tierPercent: percent,
                codeFinalCents: codeInfo ? codeInfo.finalCents : null,
                autoDiscount: autoDiscount
            });

            priceDisplay.textContent = formatPrice(newPriceCents, moneyFormat);

            if (effectivePercent > 0) {
                priceDisplay.classList.add('text-on-sale');

                if (comparePrice) {
                    comparePrice.style.display = 'inline-block';
                }

                if (badge) {
                    badge.textContent = `-${effectivePercent}%`;
                    badge.style.display = 'inline-block';
                }
            } else {
                priceDisplay.classList.remove('text-on-sale');

                if (comparePrice) {
                    comparePrice.style.display = 'none';
                }

                if (badge) {
                    badge.style.display = 'none';
                }
            }
        });
    }

    updateVisibility(variantId) {
        const variantContainers = this.container.querySelectorAll('.upsell-variant-container');
        let hasVisibleUpsells = false;

        variantContainers.forEach(container => {
            const containerVariantId = Number(container.dataset.variantId);
            if (containerVariantId === variantId) {
                container.style.display = '';
                // Check if this container actually has items
                if (container.querySelector('.upsell-item')) {
                    hasVisibleUpsells = true;
                }
            } else {
                container.style.display = 'none';

                const checkboxes = container.querySelectorAll('input[type="checkbox"]');
                checkboxes.forEach(cb => {
                    cb.checked = false;
                    cb.disabled = false;
                });
            }
        });

        // Toggle the entire group visibility
        if (hasVisibleUpsells) {
            this.container.style.display = '';
        } else {
            this.container.style.display = 'none';
        }
    }

    getVisibleContainer() {
        return Array.from(this.container.querySelectorAll('.upsell-variant-container')).find(c => c.style.display !== 'none');
    }

    getVisibleCheckboxes() {
        const visibleContainer = this.getVisibleContainer();
        if (!visibleContainer) return [];
        return Array.from(visibleContainer.querySelectorAll('[data-upsell-checkbox]'));
    }

    getSelectedUpsells() {
        const visibleContainer = this.getVisibleContainer();
        if (!visibleContainer) return [];
        return Array.from(visibleContainer.querySelectorAll('[data-upsell-checkbox]:checked'));
    }

    async handleSubmit(event) {
        const selectedUpsells = this.getSelectedUpsells();
        const count = selectedUpsells.length;

        if (count === 0) return;

        // Stop the default theme submission
        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = this.form.querySelector('[type="submit"]');
        if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.setAttribute('aria-busy', 'true');
        }

        try {
            // 1. Determine Discount Code (Foreground Priority).
            // count is always >= 1 here (early return above guards count === 0).
            const discountCode = this.tiers[tierForCount(count)].code;

            // Apply Discount if exists.
            // redirect: 'manual' is load-bearing. /discount/CODE answers with a 302 to
            // the storefront and only the Set-Cookie matters to us. Following it would
            // download a full page (~400 KB) while the add-to-cart button sits disabled.
            if (discountCode) {
                try {
                    const res = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
                        redirect: 'manual'
                    });
                    // An unfollowed redirect surfaces as type 'opaqueredirect' with
                    // status forced to 0 and ok false, which is the success case here.
                    // Verified in Chrome against the live storefront: the cookie is
                    // stored even though the response is opaque to this script.
                    if (res.type !== 'opaqueredirect' && !res.ok) {
                        console.warn('[upsell-manager] discount fetch failed', res.status, discountCode);
                    }
                } catch (e) {
                    console.warn('[upsell-manager] discount fetch error', e);
                }
            }

            const items = [];

            const formData = new FormData(this.form);
            const mainItem = {
                id: formData.get('id'),
                quantity: formData.get('quantity') || 1,
                properties: this.getProperties(formData)
            };
            // The main product keeps the plan chosen in the subscription picker. Without this, ticking an upsell
            // silently turned a subscription into a one-time purchase. Same handling as bogo-free-picker.liquid.
            // Upsells themselves stay one-time purchases (decision October 2026).
            const sellingPlan = parseInt(formData.get('selling_plan'), 10);
            if (sellingPlan > 0) {
                mainItem.selling_plan = sellingPlan;
            }
            items.push(mainItem);

            // Upsell Items
            selectedUpsells.forEach(checkbox => {
                items.push({
                    id: checkbox.value,
                    quantity: 1
                });
            });

            // 3. Add to Cart
            const sectionsToBundle = ["variant-added"];

            // Notify theme
            document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
                bubbles: true,
                detail: { sections: sectionsToBundle }
            }));

            const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    items: items,
                    sections: sectionsToBundle,
                    sections_url: window.location.pathname
                })
            });

            const responseJson = await response.json();

            if (response.ok) {
                // 4. Trigger Theme Updates
                const cartContent = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
                cartContent["sections"] = responseJson["sections"];

                const formElement = this.form;

                formElement.dispatchEvent(new CustomEvent("variant:add", {
                    bubbles: true,
                    detail: {
                        items: responseJson.items,
                        cart: cartContent
                    }
                }));

                document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
                    bubbles: true,
                    detail: {
                        baseEvent: "variant:add",
                        cart: cartContent
                    }
                }));

            } else {
                this.form.dispatchEvent(new CustomEvent("cart:error", {
                    bubbles: true,
                    detail: {
                        error: responseJson.description || responseJson.message
                    }
                }));
            }

        } catch (error) {
            console.error('Upsell Add Error:', error);
        } finally {
            if (submitButton) {
                submitButton.removeAttribute('disabled');
                submitButton.removeAttribute('aria-busy');
            }
        }
    }

    getProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
            if (key.startsWith('properties[')) {
                const propName = key.replace('properties[', '').replace(']', '');
                if (value) properties[propName] = value;
            }
        }
        return properties;
    }
}

// Auto-initialize when content loads
document.addEventListener('DOMContentLoaded', () => {
    initUpsells();
});

document.addEventListener('shopify:section:load', () => {
    initUpsells();
});

document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target) return;
    e.target.querySelectorAll('[data-upsell-container]').forEach(container => {
        if (container.upsellManager && typeof container.upsellManager.destroy === 'function') {
            container.upsellManager.destroy();
            container.upsellManager = null;
        }
    });
});

function initUpsells() {
    document.querySelectorAll('[data-upsell-container]').forEach(container => {
        const formId = container.dataset.productFormId;
        if (!formId) return;
        // Idempotent: nur neue Container bekommen einen Manager. Beim
        // shopify:section:load triggert `shopify:section:unload` vorher
        // destroy() auf den alten Containern, dadurch ist upsellManager dort
        // bereits null und der neue Container bekommt eine frische Instanz.
        if (container.upsellManager) return;
        container.upsellManager = new UpsellManager(formId, container);
    });
}

// Expose pure pricing helpers for unit tests (consistent with __dpState/__dpApi).
window.__upsellPricing = {
    tierForCount: tierForCount,
    computeUpsellPrice: computeUpsellPrice
};
